export const ZOOM_SCALE = 2;
const MAX_PREVIEW_HEIGHT_RATIO = 0.85;

/** Lens geometry in frame pixels, plus the magnified preview's size and offset. */
export interface Lens {
  x: number;
  y: number;
  left: number;
  top: number;
  width: number;
  height: number;
  previewWidth: number;
  previewHeight: number;
  gap: number;
}

/**
 * Where the hover lens and its magnified preview go for a pointer over `img`. `frame` is the
 * element the lens is positioned in and `layout` the two-column product layout. Returns null
 * when the pointer is outside the rendered image or there is no room for the preview.
 */
export function measureLens(
  pointer: { x: number; y: number },
  img: HTMLImageElement,
  frame: Element,
  layout: Element,
): Lens | null {
  if (!img.naturalWidth || !img.naturalHeight) return null;
  const rect = img.getBoundingClientRect();
  // Map the pointer to the actual rendered image, including the centered crop.
  const cover = window.getComputedStyle(img).objectFit === 'cover';
  const scale = (cover ? Math.max : Math.min)(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
  const width = img.naturalWidth * scale;
  const height = img.naturalHeight * scale;
  const left = (rect.width - width) / 2;
  const top = (rect.height - height) / 2;
  const px = pointer.x - rect.left - left;
  const py = pointer.y - rect.top - top;
  if (px < 0 || py < 0 || px > width || py > height) {
    return null;
  }

  // Align the preview with the neighboring column and respect the layout's
  // actual right edge; CSS determines both the gap and outer page margin.
  const gap = parseFloat(window.getComputedStyle(layout).columnGap) || 0;
  const layoutRight = Math.min(layout.getBoundingClientRect().right, window.innerWidth);
  const previewWidth = Math.min(rect.width, width, layoutRight - rect.right - gap);
  const previewHeight = Math.min(rect.height, height, window.innerHeight * MAX_PREVIEW_HEIGHT_RATIO);
  if (previewWidth <= 0) {
    return null;
  }
  const frameRect = frame.getBoundingClientRect();
  const minX = cover ? -left : 0;
  const minY = cover ? -top : 0;
  const visibleWidth = cover ? rect.width : width;
  const visibleHeight = cover ? rect.height : height;
  return {
    left: left + rect.left - frameRect.left,
    top: top + rect.top - frameRect.top,
    width,
    height,
    previewWidth,
    previewHeight,
    gap,
    x: Math.max(
      minX,
      Math.min(minX + visibleWidth - previewWidth / ZOOM_SCALE, px - previewWidth / (ZOOM_SCALE * 2)),
    ),
    y: Math.max(
      minY,
      Math.min(minY + visibleHeight - previewHeight / ZOOM_SCALE, py - previewHeight / (ZOOM_SCALE * 2)),
    ),
  };
}
